// Cuts a triangle mesh with a plane and returns the contour of the cut that
// passes through `seed`.
// geometry: a THREE.BufferGeometry (indexed or not), in the same coordinates
// as the plane.
// plane: { normal: [x, y, z], point: [x, y, z] }.
// seed: [x, y, z], a point on the wanted contour. A cut can have several
// contours (e.g. the other foot, or a stray piece of the mesh): the one
// nearest to the seed is kept.
// Returns a flat array of numbers, every 6 are a line segment
// (x1, y1, z1, x2, y2, z2), in no particular order; [] if the plane misses
// the mesh.
export function crossSectionContour(geometry, plane, seed) {
  const position = geometry.attributes.position;
  const index = geometry.index;
  const length = Math.hypot(...plane.normal) || 1;
  const [nx, ny, nz] = plane.normal.map((v) => v / length);
  const [px, py, pz] = plane.point;

  // signed distance of every vertex to the plane
  const dist = new Float64Array(position.count);
  for (let i = 0; i < position.count; i++) {
    dist[i] =
      (position.getX(i) - px) * nx +
      (position.getY(i) - py) * ny +
      (position.getZ(i) - pz) * nz;
  }

  // where the edge between vertices i and j crosses the plane. The two ends
  // are taken in a fixed order, so the triangles sharing the edge (which, in
  // an STL, hold their own copies of its vertices) get the very same point.
  function crossing(i, j) {
    const swap =
      position.getX(i) - position.getX(j) ||
      position.getY(i) - position.getY(j) ||
      position.getZ(i) - position.getZ(j);
    const [a, b] = swap > 0 ? [j, i] : [i, j];
    const t = dist[a] / (dist[a] - dist[b]);
    return [
      position.getX(a) + t * (position.getX(b) - position.getX(a)),
      position.getY(a) + t * (position.getY(b) - position.getY(a)),
      position.getZ(a) + t * (position.getZ(b) - position.getZ(a)),
    ];
  }

  // the cut of every triangle the plane goes through, as a segment
  const segments = [];
  const count = (index ? index.count : position.count) / 3;
  for (let t = 0; t < count; t++) {
    const ids = [0, 1, 2].map((k) =>
      index ? index.getX(t * 3 + k) : t * 3 + k,
    );
    const above = ids.map((id) => dist[id] > 0);
    if (above[0] === above[1] && above[1] === above[2]) continue;
    const points = [];
    for (let k = 0; k < 3; k++) {
      const i = ids[k];
      const j = ids[(k + 1) % 3];
      if (above[k] !== above[(k + 1) % 3]) points.push(crossing(i, j));
    }
    if (points.length === 2) segments.push(points);
  }
  if (!segments.length) return [];

  // the segments sharing an end point belong to the same contour
  const keyOf = ([x, y, z]) =>
    `${Math.round(x * 1000)},${Math.round(y * 1000)},${Math.round(z * 1000)}`;
  const at = new Map();
  segments.forEach((segment, s) => {
    segment.forEach((point) => {
      const key = keyOf(point);
      if (!at.has(key)) at.set(key, []);
      at.get(key).push(s);
    });
  });

  // the segment nearest to the seed (or the first, without a seed)
  let start = 0;
  if (Array.isArray(seed) && seed.length === 3) {
    let best = Infinity;
    segments.forEach(([a, b], s) => {
      const d = distanceToSegment(seed, a, b);
      if (d < best) {
        best = d;
        start = s;
      }
    });
  }

  // collect its whole contour
  const seen = new Set([start]);
  const queue = [start];
  while (queue.length) {
    const s = queue.pop();
    segments[s].forEach((point) => {
      (at.get(keyOf(point)) || []).forEach((next) => {
        if (!seen.has(next)) {
          seen.add(next);
          queue.push(next);
        }
      });
    });
  }
  return [...seen].flatMap((s) => [...segments[s][0], ...segments[s][1]]);
}

// distance from point `p` to the segment a-b.
function distanceToSegment(p, a, b) {
  const ab = [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
  const ap = [p[0] - a[0], p[1] - a[1], p[2] - a[2]];
  const len2 = ab[0] ** 2 + ab[1] ** 2 + ab[2] ** 2;
  const t = len2
    ? Math.min(
        1,
        Math.max(0, (ap[0] * ab[0] + ap[1] * ab[1] + ap[2] * ab[2]) / len2),
      )
    : 0;
  return Math.hypot(ap[0] - t * ab[0], ap[1] - t * ab[1], ap[2] - t * ab[2]);
}
